import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { CatalogFormField } from '../../services/serviceCatalogApi';
import { CatalogFormFieldDraft } from '../../types/CatalogFormFieldDraft';

const FIELD_TYPES: CatalogFormField['type'][] = ['text', 'number', 'textarea', 'select'];

const FIELD_TYPE_LABELS: Record<CatalogFormField['type'], string> = {
  text: 'Texto',
  number: 'Número',
  textarea: 'Texto longo',
  select: 'Seleção',
};

export function createEmptyCatalogFormFieldDraft(): CatalogFormFieldDraft {
  return {
    id: crypto.randomUUID(),
    name: '',
    label: '',
    type: 'text',
    required: false,
  };
}

type CatalogFormSchemaFieldsEditorProps = {
  fields: CatalogFormFieldDraft[];
  onChange: (fields: CatalogFormFieldDraft[]) => void;
  disabled?: boolean;
};

export function CatalogFormSchemaFieldsEditor({
  fields,
  onChange,
  disabled = false,
}: CatalogFormSchemaFieldsEditorProps) {
  function updateField(index: number, nextField: CatalogFormFieldDraft): void {
    const nextFields = [...fields];
    nextFields[index] = nextField;
    onChange(nextFields);
  }

  function removeField(index: number): void {
    onChange(fields.filter((_, fieldIndex) => fieldIndex !== index));
  }

  return (
    <div className="space-y-3">
      {fields.length === 0 ? (
        <p className="rounded-lg border border-dashed border-secondary-200 px-3 py-4 text-sm text-secondary-500">
          Nenhum campo do formulário definido.
        </p>
      ) : (
        fields.map((field, index) => (
          <div key={field.id} className="rounded-xl border border-secondary-200 bg-white p-3">
            <div className="grid gap-2 sm:grid-cols-12">
              <label className="sm:col-span-3">
                <span className="mb-1 block text-xs font-medium text-secondary-600">Identificador</span>
                <input
                  className="erp-input-compact w-full"
                  value={field.name}
                  disabled={disabled}
                  placeholder="ex.: quantidade"
                  onChange={(event) => updateField(index, { ...field, name: event.target.value })}
                />
              </label>
              <label className="sm:col-span-4">
                <span className="mb-1 block text-xs font-medium text-secondary-600">Rótulo</span>
                <input
                  className="erp-input-compact w-full"
                  value={field.label}
                  disabled={disabled}
                  placeholder="ex.: Quantidade"
                  onChange={(event) => updateField(index, { ...field, label: event.target.value })}
                />
              </label>
              <label className="sm:col-span-3">
                <span className="mb-1 block text-xs font-medium text-secondary-600">Tipo</span>
                <select
                  className="erp-input-compact w-full"
                  value={field.type}
                  disabled={disabled}
                  onChange={(event) =>
                    updateField(index, {
                      ...field,
                      type: event.target.value as CatalogFormField['type'],
                      options: event.target.value === 'select' ? field.options ?? [''] : undefined,
                    })
                  }
                >
                  {FIELD_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {FIELD_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-end justify-between gap-2 sm:col-span-2">
                <label className="erp-label-inline pb-2">
                  <input
                    type="checkbox"
                    checked={Boolean(field.required)}
                    disabled={disabled}
                    onChange={(event) => updateField(index, { ...field, required: event.target.checked })}
                  />
                  Obrigatório
                </label>
                <button
                  type="button"
                  className="rounded-lg p-2 text-secondary-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  disabled={disabled}
                  title="Remover campo"
                  aria-label="Remover campo"
                  onClick={() => removeField(index)}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {field.type === 'select' ? (
              <label className="mt-3 block">
                <span className="mb-1 block text-xs font-medium text-secondary-600">
                  Opções (uma por linha)
                </span>
                <textarea
                  className="erp-input min-h-[72px] w-full text-sm"
                  value={(field.options ?? []).join('\n')}
                  disabled={disabled}
                  placeholder={'Opção 1\nOpção 2'}
                  onChange={(event) =>
                    updateField(index, {
                      ...field,
                      options: event.target.value.split('\n'),
                    })
                  }
                />
              </label>
            ) : null}
          </div>
        ))
      )}

      <button
        type="button"
        className="erp-btn-secondary erp-btn-sm"
        disabled={disabled}
        onClick={() => onChange([...fields, createEmptyCatalogFormFieldDraft()])}
      >
        <PlusIcon className="mr-1 inline h-4 w-4" />
        Adicionar campo
      </button>
    </div>
  );
}
